import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { adFormats, partnerApps, type AdFormatId, type PartnerAppId } from '@/mocks/advertise';

const AD = '#2f51d6';

/** The advertiser's sample ad, drawn the way each format appears inside a partner app. */
function SampleAd({ format, onClick }: { format: AdFormatId; onClick: () => void }) {
  const brand = (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white/90">
      <i className="ri-cup-fill" /> Sunrise Coffee
    </span>
  );
  const cta = 'mt-1.5 inline-flex rounded-full bg-white px-2.5 py-1 text-[10px] font-bold';
  switch (format) {
    case 'banner':
      return (
        <button type="button" onClick={onClick} className="w-full rounded-xl p-3 text-left text-white cursor-pointer hover:brightness-110" style={{ background: AD }}>
          {brand}
          <div className="text-sm font-extrabold leading-tight mt-0.5">Buy 1 get 1 free latte</div>
          <span className={cta} style={{ color: AD }}>Get offer</span>
        </button>
      );
    case 'native':
      return (
        <button type="button" onClick={onClick} className="w-full rounded-xl bg-white border border-[#e6e8ee] text-left overflow-hidden cursor-pointer hover:shadow-md">
          <div className="flex items-center gap-2 px-3 pt-2.5">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs" style={{ background: AD }}><i className="ri-cup-fill" /></span>
            <span className="text-[11px] font-semibold text-[#1f2329]">Sunrise Coffee</span>
            <span className="ml-auto text-[9px] text-[#8f959e]">Sponsored</span>
          </div>
          <p className="px-3 pt-1.5 text-[11px] text-[#41464f]">New iced latte is here. Members get 1 free with 500 points.</p>
          <div className="mx-3 my-2 h-16 rounded-lg bg-gradient-to-br from-[#2f51d6] to-[#7b8cff]" />
          <div className="px-3 pb-2.5"><span className="inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold text-white" style={{ background: AD }}>Learn more</span></div>
        </button>
      );
    case 'survey':
      return (
        <div className="rounded-xl bg-white border border-[#2f51d6] p-3">
          <div className="text-[10px] font-bold" style={{ color: AD }}>Sponsored by Sunrise Coffee · +50 points</div>
          <div className="mt-1 text-xs font-semibold text-[#1f2329]">How often do you buy coffee?</div>
          <div className="mt-2 space-y-1.5">
            {['Every day', 'A few times a week', 'Rarely'].map((t) => (
              <button key={t} type="button" onClick={onClick} className="w-full text-left rounded-lg border border-[#e6e8ee] px-2.5 py-1.5 text-[11px] text-[#41464f] hover:border-[#2f51d6] hover:text-[#2f51d6] cursor-pointer">
                {t}
              </button>
            ))}
          </div>
        </div>
      );
    case 'reward':
      return (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onClick} className="rounded-xl bg-white border-2 border-[#2f51d6] p-2 text-left cursor-pointer hover:shadow-md">
            <div className="h-14 rounded-lg flex items-center justify-center text-white" style={{ background: AD }}><i className="ri-cup-fill text-2xl" /></div>
            <div className="mt-1.5 text-[11px] font-semibold text-[#1f2329]">Free latte</div>
            <div className="text-[10px]" style={{ color: AD }}>800 pts · Sunrise Coffee</div>
          </button>
          <div className="rounded-xl bg-white border border-[#e6e8ee] p-2 opacity-70">
            <div className="h-14 rounded-lg bg-[#f2f3f5] flex items-center justify-center text-[#a3a8b0]"><i className="ri-smartphone-line text-2xl" /></div>
            <div className="mt-1.5 text-[11px] font-semibold text-[#1f2329]">$1 top-up</div>
            <div className="text-[10px] text-[#8f959e]">1,000 pts</div>
          </div>
        </div>
      );
    case 'fullscreen':
      return (
        <button type="button" onClick={onClick} className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-white cursor-pointer" style={{ background: `linear-gradient(160deg, ${AD}, #7b5cff)` }}>
          <span className="absolute top-3 right-3 text-xs text-white/70">Close in 3s</span>
          {brand}
          <i className="ri-cup-fill text-5xl" />
          <span className="text-lg font-extrabold">Iced latte week</span>
          <span className="rounded-full bg-white px-4 py-1.5 text-xs font-bold" style={{ color: AD }}>See the offer</span>
        </button>
      );
    case 'push':
      return (
        <button type="button" onClick={onClick} className="absolute top-2 inset-x-2 z-10 rounded-2xl bg-white/95 shadow-lg border border-[#e6e8ee] p-2.5 flex gap-2 text-left cursor-pointer">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: AD }}><i className="ri-cup-fill" /></span>
          <span className="min-w-0">
            <span className="block text-[11px] font-bold text-[#1f2329]">Sunrise Coffee · now</span>
            <span className="block text-[11px] text-[#41464f] leading-snug">Your free latte is waiting. Tap to redeem with 500 points.</span>
          </span>
        </button>
      );
  }
}

/** A partner app screen on a phone, with the sample ad placed where that format appears. */
function PartnerPhone({ app, format, onAdClick }: { app: PartnerAppId; format: AdFormatId; onAdClick: () => void }) {
  const info = partnerApps.find((a) => a.id === app)!;
  const content = (
    <div className="space-y-2">
      {[0, 1].map((i) => (
        <div key={i} className="rounded-xl bg-white border border-[#eef0f3] p-2.5 flex gap-2 items-center">
          <span className="w-8 h-8 rounded-lg bg-[#f2f3f5]" />
          <div className="flex-1 space-y-1"><span className="block h-2 w-3/4 rounded bg-[#e6e8ee]" /><span className="block h-2 w-1/2 rounded bg-[#eef0f3]" /></div>
        </div>
      ))}
    </div>
  );
  const inline = format === 'banner' || format === 'native' || format === 'survey' || format === 'reward';
  return (
    <div className="relative mx-auto w-[260px] h-[500px] rounded-[36px] border-[6px] border-[#1f2329] bg-[#f5f6f8] overflow-hidden shadow-xl">
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-4 rounded-full bg-[#1f2329] z-20" />
      <div className="h-full overflow-hidden flex flex-col">
        <div className="pt-8 pb-3 px-4 text-white" style={{ background: info.color }}>
          <div className="flex items-center gap-1.5 text-sm font-bold"><i className={info.icon} /> {info.name}</div>
          <div className="text-[10px] text-white/80">{info.tagline}</div>
        </div>
        <div className="p-3 space-y-2.5 flex-1">
          {format === 'reward' && <div className="text-[11px] font-semibold text-[#1f2329]">Rewards you can redeem</div>}
          {inline && <SampleAd format={format} onClick={onAdClick} />}
          {content}
        </div>
      </div>
      {!inline && <SampleAd format={format} onClick={onAdClick} />}
    </div>
  );
}

interface LiveDemoProps {
  open: boolean;
  initialFormat: AdFormatId;
  onClose: () => void;
}

export default function LiveDemo({ open, initialFormat, onClose }: LiveDemoProps) {
  const [format, setFormat] = useState<AdFormatId>(initialFormat);
  const [app, setApp] = useState<PartnerAppId>('survey');
  const [views, setViews] = useState(0);
  const [clicks, setClicks] = useState(0);
  const [lastEvent, setLastEvent] = useState('');

  // Each time the ad is shown (new format or app) counts as one view.
  // The page mounts this component fresh for every demo, so counters start at zero.
  useEffect(() => {
    setViews((v) => v + 1);
    setLastEvent('View recorded: the ad was shown to a member');
  }, [format, app]);

  const ctr = views ? ((clicks / views) * 100).toFixed(1) : '0.0';
  const current = adFormats.find((f) => f.id === format)!;

  return (
    <Modal open={open} onClose={onClose} title="Live demo" subtitle="Try an ad inside a partner app. Click the ad to record a click." maxWidth="max-w-3xl">
      <div className="grid md:grid-cols-[1fr_280px] gap-5">
        <div className="space-y-4 min-w-0">
          <div>
            <div className="text-xs font-semibold text-[#646a73] mb-1.5">Ad format</div>
            <div className="flex flex-wrap gap-1.5">
              {adFormats.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFormat(f.id)}
                  className={`h-8 px-3 rounded-full text-xs font-semibold cursor-pointer transition-colors ${
                    f.id === format ? 'bg-[#2f51d6] text-white' : 'bg-[#f2f3f5] text-[#41464f] hover:bg-[#e8edff]'
                  }`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[#646a73] mb-1.5">Partner app</div>
            <div className="inline-flex p-1 rounded-full bg-[#f2f3f5]">
              {partnerApps.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setApp(a.id)}
                  className={`h-8 px-4 rounded-full text-xs cursor-pointer inline-flex items-center gap-1 ${a.id === app ? 'bg-white shadow-sm font-semibold text-[#1f2329]' : 'text-[#646a73]'}`}
                >
                  <i className={a.icon} style={{ color: a.color }} /> {a.name}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-[#e6e8ee] p-4">
            <div className="text-xs font-semibold text-[#646a73]">What you see in your dashboard</div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-center">
              {[
                { k: 'Views', v: views },
                { k: 'Clicks', v: clicks },
                { k: 'Click rate', v: `${ctr}%` },
              ].map((s) => (
                <div key={s.k}>
                  <div className="text-2xl font-extrabold text-[#1f2329] tabular-nums">{s.v}</div>
                  <div className="text-[11px] text-[#8f959e]">{s.k}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-[#2f51d6] min-h-[16px]" aria-live="polite">{lastEvent}</p>
            <button
              type="button"
              onClick={() => {
                setViews((v) => v + 1);
                setLastEvent('View recorded: another member opened the app');
              }}
              className="mt-2 text-xs font-semibold text-[#646a73] hover:text-[#1f2329] cursor-pointer inline-flex items-center gap-1"
            >
              <i className="ri-refresh-line" /> Show to another member
            </button>
          </div>

          <div className="text-xs text-[#646a73]">
            <b className="text-[#1f2329]">{current.name}:</b> {current.summary} <span className="text-[#8f959e]">({current.plans})</span>
          </div>
        </div>

        <PartnerPhone
          app={app}
          format={format}
          onAdClick={() => {
            setClicks((c) => c + 1);
            setLastEvent('Click recorded: the member opened your offer');
          }}
        />
      </div>
    </Modal>
  );
}
